import { ApiError, type ApiClient } from '../api/client';
import type { DraftStore, EditorDraft } from '../views/line-draft-store';

/**
 * Brouillon d'une fiche (« Enregistrer et sortir » du bandeau de formulaire, 2026-10-19 ; contrat
 * `work-item-draft`). Comme le « Conserver » du v1 : la saisie en cours est gardée au serveur
 * (`SaveWorkItemDraft`), la tâche reste en cours, rien d'autre ne se passe ; elle se reprend d'un autre
 * poste ou de la tablette (`GetWorkItemDraft`). Même chemin que la tablette (`task_draft_repository.dart`),
 * la copie du navigateur (brouillon local d'ADR-146, addendum (4)) n'en est que le tampon hors ligne.
 */

/** Saisie en cours d'une fiche : jamais une valeur validée. */
export interface FormDraftValues {
  answers: Record<string, unknown>;
  evidence: Record<string, string[]>;
  pageId?: string;
  /**
   * Réponses suggérées par une réaction et non modifiées (work-item-draft 1.1.0, ADR-146 addendum (21)) :
   * reprises du brouillon, elles restent des suggestions.
   */
  origins?: DraftOrigins;
}

/** Origines gardées au brouillon : chemin de réponse → `{ origin: 'suggested', source: 'reaction:<id>' }`. */
export type DraftOrigins = Record<string, { origin: 'suggested'; source: string }>;

const ORIGIN_PATH = /^answers\.[a-z][a-z0-9_]*(\.[0-9]+\.[a-z][a-z0-9_]*)?$/;

/** Origines lisibles d'un brouillon : chemins valides, marque `suggested`, source d'une réaction ; le reste est ignoré. */
export function draftOrigins(raw: unknown): DraftOrigins {
  const out: DraftOrigins = {};
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [path, origin] of Object.entries(raw as Record<string, unknown>)) {
    const value = origin as { origin?: unknown; source?: unknown } | null;
    if (!ORIGIN_PATH.test(path) || value === null || typeof value !== 'object' || value.origin !== 'suggested' || typeof value.source !== 'string' || !/^reaction:[a-z][a-z0-9_]*$/.test(value.source)) continue;
    out[path] = { origin: 'suggested', source: value.source };
  }
  return out;
}

export interface FormDraft extends FormDraftValues {
  /** Version du brouillon au serveur : celle que le prochain enregistrement attend. */
  version: number;
  savedAt: string;
}

export type DraftSaveOutcome = 'saved' | 'newer_elsewhere';

type Client = Pick<ApiClient, 'command' | 'query'>;

/** Le brouillon à reprendre ; un brouillon saisi sur une autre version du formulaire n'est pas repris en silence. */
export async function loadFormDraft(client: Client, workItemId: string): Promise<FormDraft | null> {
  const reply = await client.query<{ draft: null | { values?: { answers?: unknown; evidence?: unknown; answer_origins?: unknown }; position?: { page_id?: string }; version?: number; saved_at?: string; client_saved_at?: string }; form_matches?: boolean }>('GetWorkItemDraft', { work_item_id: workItemId });
  const draft = reply.data.draft;
  if (draft === null || reply.data.form_matches === false) return null;
  const answers = draft.values?.answers;
  if (answers === null || typeof answers !== 'object' || Array.isArray(answers)) return null;
  const evidence: Record<string, string[]> = {};
  for (const [field, items] of Object.entries((draft.values?.evidence ?? {}) as Record<string, unknown>)) {
    if (Array.isArray(items)) evidence[field] = items.filter((item): item is string => typeof item === 'string');
  }
  const origins = draftOrigins(draft.values?.answer_origins);
  return {
    answers: answers as Record<string, unknown>,
    evidence,
    ...(Object.keys(origins).length === 0 ? {} : { origins }),
    ...(draft.position?.page_id === undefined ? {} : { pageId: draft.position.page_id }),
    version: draft.version ?? 1,
    savedAt: String(draft.client_saved_at ?? draft.saved_at ?? ''),
  };
}

/** « Enregistrer et sortir » : la saisie part au serveur, à la version du brouillon lu (concurrence optimiste). */
export async function saveFormDraft(client: Client, workItemId: string, values: FormDraftValues, version: number | undefined, now: Date = new Date()): Promise<DraftSaveOutcome> {
  try {
    await client.command('SaveWorkItemDraft', {
      payload: {
        work_item_id: workItemId,
        values: { answers: values.answers, evidence: values.evidence, ...(values.origins === undefined || Object.keys(values.origins).length === 0 ? {} : { answer_origins: values.origins }) },
        ...(values.pageId === undefined ? {} : { position: { page_id: values.pageId } }),
        client_saved_at: now.toISOString(),
      },
      ...(version === undefined ? {} : { expected_version: version }),
    });
    return 'saved';
  } catch (caught) {
    if (caught instanceof ApiError && caught.code === 'EXPECTED_VERSION_MISMATCH') return 'newer_elsewhere';
    throw caught;
  }
}

/** « Recommencer » : le brouillon du serveur est effacé (DiscardWorkItemDraft) ; la tâche, elle, ne change pas. */
export async function discardFormDraft(client: Client, workItemId: string, version: number | undefined): Promise<void> {
  await client.command('DiscardWorkItemDraft', { payload: { work_item_id: workItemId }, ...(version === undefined ? {} : { expected_version: version }) });
}

/** Le brouillon du serveur au format du brouillon local (`EditorDraft`), pour la reprise du formulaire. */
export function asEditorDraft(draft: FormDraft): EditorDraft {
  return { saved_at: draft.savedAt, values: { answers: draft.answers, evidence: draft.evidence, ...(draft.origins === undefined ? {} : { answer_origins: draft.origins }), ...(draft.pageId === undefined ? {} : { page_id: draft.pageId }) }, lines: [] };
}

/**
 * Brouillon à reprendre : le plus récent de la copie du navigateur et de celle du serveur (lue avant
 * d'ouvrir la fiche). Les écritures restent celles du stockage local ; l'envoi au serveur est `saveFormDraft`.
 */
export function withServerDraft(local: DraftStore, server: FormDraft | null): DraftStore {
  return {
    load(key) {
      const kept = local.load(key);
      if (server === null) return kept;
      if (kept !== null && kept.saved_at > server.savedAt) return kept;
      return asEditorDraft(server);
    },
    save: (key, draft) => local.save(key, draft),
    clear: (key) => local.clear(key),
  };
}
